import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';

import { PageEvent } from '@angular/material/paginator';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  of,
  Subscription,
} from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { indicate } from 'src/app/operators';
import { PipingAndValvesNavigationService } from 'src/app/piping-valves/services/piping-and-valves-navigation.service';
import { UserService } from 'src/app/core/services/user.service';
import { FilterablePropertyFactory } from 'src/app/shared/components/factory/filterable-property-factory.service';
import {
  EsraPipingAPIClient,
  FilterableProperty,
  FilterType,
  PipingFilterCategory,
  PipingObjectType,
  PipingSearchFilterModel,
  PipingSearchResultViewModel,
  SearchRequest,
} from 'src/app/shared/models/autogenerated-piping';
import { TechnologyEnum } from 'src/app/shared/models/technology-enum';
import { CelsiusToFahrenheitPipe } from 'src/app/shared/pipes/celcius-to-fahrenheit.pipe';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { environment } from 'src/environments/environment';
import { MatPaginatorGoToComponent } from '../../../shared/components/mat-paginator-go-to/mat-paginator-go-to.component';
import { FilterDataService } from '../../services/filter.service';
import { DynamicPipingFiltersService } from '../../services/dynamic-piping-filters.service';
import { RangeFilterHelper } from '../common/range-filter.helper';

@Component({
    selector: 'app-piping-search',
    templateUrl: './piping-search.component.html',
    styleUrls: ['./piping-search.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class PipingSearchComponent implements OnInit, OnDestroy {
  constructor(
    private readonly esraPipingAPIClient: EsraPipingAPIClient,
    private readonly dynamicPipingFiltersService: DynamicPipingFiltersService,
    private readonly filterDataService: FilterDataService,
    public rangeFilterHelper: RangeFilterHelper,
    public navigator: PipingAndValvesNavigationService,
    private readonly snackBarService: SnackBarService,
    private readonly userService: UserService,
    private readonly cd: ChangeDetectorRef
  ) { }

  searchText = '';
  currentPage = 0;
  technology: TechnologyEnum = TechnologyEnum.PIPES;
  lastPage: number;
  INTMAXCSHARP = 2147483647;
  isFilterBeingRemoved: boolean;

  totalCount: number;
  pageSize = 10;

  // object types
  _both = PipingObjectType._0;
  _pipe = PipingObjectType._1;
  _valve = PipingObjectType._2;

  // filter categories
  _defaultFilter = PipingFilterCategory._0;
  _commonFilter = PipingFilterCategory._3;
  _pipeFilter = PipingFilterCategory._1;
  _valveFilter = PipingFilterCategory._2;

  // input ids
  _pipesCheckBoxId = 'pipes-check-button';
  _valvesCheckBoxId = 'valves-check-button';

  // temperature units
  _fahrenheit = 'fahrenheit';
  _celsius = 'celcius';

  // comparison units
  _equals = 'equals (eq)';
  _greaterThan = 'greater than (gt)';
  _greaterThanEquals = 'greater or equals (ge)';
  _lessThan = 'less than (lt)';
  _lessThanEquals = 'less than or equals (le)';

  _defaultNominalCorrosionAllowanceComparisonSelectionString = this._equals;
  _defualtMaximumTemperatureComparisonSelectionString = this._greaterThanEquals;
  _defualtMinimumTemperatureComparisonSelectionString = this._lessThanEquals;
  nominalCorrosionAllowanceComparisonOptions: Array<string> = [
    this._equals,
    this._greaterThanEquals,
    this._greaterThan,
    this._lessThan,
    this._lessThanEquals,
  ];

  maximumTemperatureComparisonOptions: Array<string> = [
    this._greaterThanEquals,
    this._greaterThan,
    this._equals,
  ];

  minimumTemperatureComparisonOptions: Array<string> = [
    this._lessThanEquals,
    this._lessThan,
    this._equals,
  ];

  selectedNominalCorrosionAllowanceComparisonOption =
    this._defaultNominalCorrosionAllowanceComparisonSelectionString;
  selectedMaximumTemperatureComparisonOption =
    this._defualtMaximumTemperatureComparisonSelectionString;
  selectedMinimumTemperatureComparisonOption =
    this._defualtMinimumTemperatureComparisonSelectionString;

  selectedTemperatureUnit = this._fahrenheit;

  typeSelectedSubject = new BehaviorSubject<PipingObjectType>(this._both);
  typeSelected$ = this.typeSelectedSubject.asObservable();
  typeSelected: PipingObjectType = this._both;

  private readonly filtersForTypeSubject = new BehaviorSubject<PipingSearchFilterModel[]>([]);
  filtersForType$ = this.filtersForTypeSubject.asObservable();
  facetValues = new BehaviorSubject<PipingSearchFilterModel[]>([]);
  facetValues$ = this.facetValues.asObservable();
  private readonly metadataReadySubject = new BehaviorSubject<boolean>(false);
  metadataReady$ = this.metadataReadySubject.asObservable();
  latestFilterSelection$ = new BehaviorSubject<string>('');
  filters$: Observable<PipingSearchFilterModel[]> = this.getPipingFilters();
  pipeResults$: Observable<PipingSearchResultViewModel[]>;
  filterableProperties: FilterableProperty[];
  filterablePropertiesFlattened: FilterableProperty[];
  pipeFilterCount: number;
  valveFilterCount: number;
  commonFilterCount: number;
  inputNumStr: string;
  pipe = new CelsiusToFahrenheitPipe();
  searchResultObjects: PipingSearchResultViewModel[];

  //indicator subjects
  searching$ = new BehaviorSubject<boolean>(false);
  loadingFilters$ = new BehaviorSubject<boolean>(false);
  exportingPdf$ = new BehaviorSubject<boolean>(false);

  private readonly subscription = new Subscription();

  pageSizes = [2, 5, 10, 25, 50, 100];
  @ViewChild('paginator') paginator: MatPaginatorGoToComponent;
  @ViewChild('pipesCheckButton')
  pipesRadioButtonInput: ElementRef<HTMLInputElement>;
  @ViewChild('valvesCheckButton')
  valvesRadioButtonInput: ElementRef<HTMLInputElement>;
  @ViewChild('fahrenheitCheckButton')
  fahrenheitRadioButtonInput: ElementRef<HTMLInputElement>;
  @ViewChild('celsiusCheckButton')
  celsiusRadioButtonInput: ElementRef<HTMLInputElement>;

  filterDisplayArchivedItems = false;
  inProduction: boolean;

  /** When true, the search includes archived, draft, and retired items. Effective for admin users only. */
  includeNonCurrent: boolean = false;

  isPipingAndValvesAdmin$: Observable<boolean>;
  private readonly filterMetadataByPropertyName: Map<string, PipingSearchFilterModel> = new Map<string, PipingSearchFilterModel>();
  private readonly filterTypeByPropertyName: Map<string, string> = new Map<string, string>();
  private latestSearchFiltersFromResponse: PipingSearchFilterModel[] = [];
  private readonly showNameOrTagFilter: boolean = false;
  private static readonly HIDDEN_FILTER_PROPERTY_NAMES: Set<string> = new Set<string>([
    'ObjectType',
  ]);

  ngOnInit() {
    this.inProduction = environment.production;
    this.isPipingAndValvesAdmin$ = this.userService.isPipingAndValvesAdminObservable();

    this.subscription.add(
      this.typeSelected$.pipe(
        tap((type: PipingObjectType) => {
          this.typeSelected = type;
          this.loadFilterGroupingMetadata(type);
        })
      ).subscribe()
    );

    this.subscription.add(
      this.filterDataService.currentFiltersSelection$
        .pipe(
          map((filterSelection) => {
            this.filterableProperties = Array.from(filterSelection.values());
            this.filterablePropertiesFlattened = this.filterableProperties;
            this.currentPage = 0;
            this.search();
          })
        )
        .subscribe()
    );

    this.cd.detectChanges();
  }

  public showPipeFilterGroup(): boolean {
    return this.typeSelected !== this._valve;
  }

  public showValveFilterGroup(): boolean {
    return this.typeSelected !== this._pipe;
  }

  private loadFilterGroupingMetadata(type: PipingObjectType): void {
    this.metadataReadySubject.next(false);

    this.subscription.add(
      this.dynamicPipingFiltersService
        .loadFilterMetadata(type)
        .pipe(indicate(this.loadingFilters$))
        .subscribe({
          next: (filters: PipingSearchFilterModel[]) => {
            this.filterMetadataByPropertyName.clear();
            for (const filter of filters ?? []) {
              const filterPropertyName: string = filter.filterPropertyName ?? '';
              if (!filterPropertyName) {
                continue;
              }

              this.filterMetadataByPropertyName.set(filterPropertyName, filter);
            }

            this.metadataReadySubject.next(true);
            this.hydrateFiltersFromSearchResponse();
          },
          error: () => {
            this.filterMetadataByPropertyName.clear();
            this.metadataReadySubject.next(true);
            this.hydrateFiltersFromSearchResponse();
          },
        })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.resetFilters();
  }

  getFiltersForType(): Observable<PipingSearchFilterModel[]> {
    return combineLatest([this.typeSelected$, this.filtersForType$]).pipe(
      map(([type, filters]: [PipingObjectType, PipingSearchFilterModel[]]) => {
        this.typeSelected = type;
        return filters;
      })
    );
  }

  callSearch(e: string): void {
    this.searchText = e;
    this.search();
  }

  private search(): void {
    const request: SearchRequest = new SearchRequest();
    request.perPage = this.pageSize;
    request.page = this.currentPage;
    request.mainSearch = this.wrapSearchTextInWildcardsConditionally(this.searchText);
    request.filters = this.filterableProperties;
    request.orderBy = 'NameOrTag asc';

    this.runLegacySearch(request);
  }

  private runLegacySearch(request: SearchRequest): void {
    this.technology = Number(TechnologyEnum.PIPES);
    this.subscription.add(
      this.esraPipingAPIClient
        .pdfSearchPipingAndValves(this.includeNonCurrent, request)
        .pipe(
          indicate(this.searching$),
          indicate(this.loadingFilters$),
          tap((response) => {
            this.latestSearchFiltersFromResponse = response.searchFilters ?? [];
            this.pipeResults$ = of(response.results);
            this.searchResultObjects = response.results;
            this.hydrateFiltersFromSearchResponse();
            this.totalCount = response.totalResults;
            this.lastPage = Math.ceil(this.totalCount / this.pageSize);
          })
        )
        .subscribe()
    );
  }

  /** Called when the include-non-current toggle changes. Resets to page 1 and re-runs the search. */
  onIncludeNonCurrentChange(): void {
    this.currentPage = 0;
    this.search();
  }

  public addSearchResultsToWorkspace() {
    const request: SearchRequest = new SearchRequest();
    request.perPage = this.INTMAXCSHARP;
    request.page = 0;
    request.mainSearch = this.searchText;
    request.filters = this.filterableProperties;

    this.subscription.add(
      this.esraPipingAPIClient
        .addSearchRequestPipeAndValveClassesToWorkspace(request)
        .pipe(
          indicate(this.searching$),
          tap({
            next: () => {
              this.snackBarService.showSnackBar(
                true,
                'All search results were added to your workspace'
              );
            },
            error: (err) => {
              this.snackBarService.showError(err.message);
            },
          })
        )
        .subscribe()
    );
  }

  private createFilterableProperty(
    filterType: FilterType,
    propertyName: string,
    filterDisplayName: string,
    filterOptions: string[]
  ): FilterableProperty {
    const filterableProperty = new FilterableProperty();
    filterableProperty.filterType = filterType;
    filterableProperty.propertyName = propertyName;
    filterableProperty.displayName = filterDisplayName;
    filterableProperty.filterOptions = filterOptions;
    return filterableProperty;
  }

  private setObjectTypeFilter() {
    if (this.typeSelected == this._pipe) {
      const pipeSet = new Set<string>(['PIPE']);
      this.updateCheckboxFilterSelection(pipeSet, 'ObjectType', 'ObjectType');
    } else if (this.typeSelected == this._valve) {
      const valveSet = new Set<string>(['VALVE']);
      this.updateCheckboxFilterSelection(valveSet, 'ObjectType', 'ObjectType');
    }
  }

  public updateCheckboxFilterSelection(
    event: Set<string>,
    filterablePropertyName: string,
    filterDisplayName: string
  ) {
    this.latestFilterSelection$.next(filterablePropertyName);
    this.currentPage = 0;
    if (event.size === 0) {
      this.isFilterBeingRemoved = true;
      this.filterDataService.removeAllFilterValues(filterablePropertyName);
    } else {
      this.isFilterBeingRemoved = false;
      const requestFilterType: FilterType = this.resolveRequestFilterType(filterablePropertyName, FilterType._1);
      const filterableProperty = this.createFilterableProperty(
        requestFilterType,
        filterablePropertyName,
        filterDisplayName,
        Array.from(event)
      );

      this.filterDataService.setFilterValue(filterableProperty);
    }
  }

  public notTrueFalseOptionOnly(filter: any): boolean {
    const optionsLowerCased = filter.options.map((filterOption: string) =>
      filterOption.toLowerCase()
    );
    return !(
      optionsLowerCased.length == 2 &&
      optionsLowerCased.includes('true') &&
      optionsLowerCased.includes('false')
    );
  }

  public updateAdditionalRadioFilterSelection(
    event: Set<string>,
    filterablePropertyName: string,
    filterDisplayName: string
  ) {
    // TODO: implement updateAdditionalRadioFilterSelection
  }

  public updateAdditionalTextFilterSelection(
    inputNumStr: string,
    filterablePropertyName: string,
    filterDisplayName: string,

  ) {
    this.currentPage = 0;

    if (inputNumStr === '') {
      this.filterDataService.removeAllFilterValues(filterablePropertyName);
    } else {
      const selectedOperatorString = this.getSelectedOperatorStringFromSymbol(this.selectedNominalCorrosionAllowanceComparisonOption);
      this.latestFilterSelection$.next(filterablePropertyName);

      const filterOptions = [selectedOperatorString + inputNumStr];
      const requestFilterType: FilterType = this.resolveRequestFilterType(filterablePropertyName, FilterType._7);

      const filterableProperty = this.createFilterableProperty(
        requestFilterType,
        filterablePropertyName,
        filterDisplayName,
        filterOptions
      );

      this.filterDataService.setFilterValue(filterableProperty);
      this.selectedNominalCorrosionAllowanceComparisonOption = '';
    }
  }

  public updateNumericalFilterSelection(
    event: string,
    filterablePropertyName: string,
    filterDisplayName: string,
    selectedComparisonOperator: string
  ) {
    let selectedValue = event;

    if (selectedValue === '') {
      this.filterDataService.removeAllFilterValues(filterablePropertyName);
    } else {
      const selectedOperatorStr = this.getSelectedOperatorStringFromSymbol(selectedComparisonOperator);

      if (this.selectedTemperatureUnit === this._celsius) {
        selectedValue = this.pipe.transform(parseFloat(selectedValue));
      }

      this.latestFilterSelection$.next(filterablePropertyName);
      const requestFilterType: FilterType = this.resolveRequestFilterType(filterablePropertyName, FilterType._7);
      const filterableProperty = this.createFilterableProperty(
        requestFilterType,
        filterablePropertyName,
        filterDisplayName,
        [selectedOperatorStr + selectedValue]
      );

      this.filterDataService.setFilterValue(filterableProperty);
    }
  }

  public updateNominalCorrosionAllowanceComparisonOption(event: any) {
    this.selectedNominalCorrosionAllowanceComparisonOption = event;
  }

  public updateMaximumTemperatureComparisonOption(event: any) {
    this.selectedMaximumTemperatureComparisonOption = event;
  }

  public updateMinimumTemperatureComparisonOption(event: any) {
    this.selectedMinimumTemperatureComparisonOption = event;
  }

  public updateSingleSliderFilterSelection(
    event: string,
    filterablePropertyName: string,
    filterDisplayName: string,
    filterType: string
  ) {
    // TODO: implement updateSingleSliderFilterSelection
  }

  public updateRangeFilterSelection(
    event: string,
    filterablePropertyName: string,
    filterDisplayName: string,
    filterType: string
  ) {
    // TODO: implement updateRangeFilterSelection
  }

  public popClearedFilter($event: any, filterPropertyName: string) {
    if (this.latestFilterSelection$.getValue() === filterPropertyName) {
      this.latestFilterSelection$.next('');
    }
  }

  public trackByFilterPropertyName(index: number, filter: PipingSearchFilterModel): string {
    return filter.filterPropertyName ?? `filter-${index}`;
  }

  private normalizePdfSearchFilters(filters: PipingSearchFilterModel[]): PipingSearchFilterModel[] {
    const normalizedFilters: PipingSearchFilterModel[] = [];
    const seenFilterPropertyNames: Set<string> = new Set<string>();

    for (const filter of filters ?? []) {
      const normalizedFilterPropertyName: string = this.normalizePdfFilterPropertyName(
        filter.filterPropertyName ?? '',
        filter.filterDisplayName ?? ''
      );

      if (!normalizedFilterPropertyName || seenFilterPropertyNames.has(normalizedFilterPropertyName)) {
        continue;
      }

      if (PipingSearchComponent.HIDDEN_FILTER_PROPERTY_NAMES.has(normalizedFilterPropertyName)) {
        continue;
      }

      if (!this.showNameOrTagFilter && normalizedFilterPropertyName === 'NameOrTag') {
        continue;
      }

      const filterMetadata: PipingSearchFilterModel | undefined = this.filterMetadataByPropertyName.get(normalizedFilterPropertyName);
      if (!filterMetadata) {
        continue;
      }

      const mergedFilterDisplayName: string = filter.filterDisplayName ?? filterMetadata.filterDisplayName ?? '';
      const mergedFilterType: string = filter.filterType ?? filterMetadata.filterType ?? '';
      const hasValidMetadata: boolean = !!mergedFilterDisplayName && !!mergedFilterType;
      if (!hasValidMetadata) {
        continue;
      }

      filter.filterPropertyName = normalizedFilterPropertyName;
      filter.filterDisplayName = mergedFilterDisplayName;
      filter.filterType = mergedFilterType;
      this.filterTypeByPropertyName.set(normalizedFilterPropertyName, filter.filterType ?? '');
      filter.filterCategory = this.resolveFilterCategory(filterMetadata);
      seenFilterPropertyNames.add(normalizedFilterPropertyName);
      normalizedFilters.push(filter);
    }

    return normalizedFilters;
  }

  private normalizePdfFilterPropertyName(filterPropertyName: string, filterDisplayName: string): string {
    void filterDisplayName;
    return filterPropertyName;
  }

  private resolveFilterCategory(filter: PipingSearchFilterModel): PipingFilterCategory {
    const rawFilterCategory: number = Number(filter.filterCategory);
    if (
      rawFilterCategory === this._defaultFilter
      || rawFilterCategory === this._pipeFilter
      || rawFilterCategory === this._valveFilter
      || rawFilterCategory === this._commonFilter
    ) {
      return rawFilterCategory as PipingFilterCategory;
    }

    return this._defaultFilter;
  }

  private resolveRequestFilterType(filterPropertyName: string, fallbackFilterType: FilterType): FilterType {
    const filterTypeName: string = this.filterTypeByPropertyName.get(filterPropertyName) ?? '';

    if (filterTypeName === 'BOOLEAN') {
      return FilterType._6;
    }

    if (filterTypeName === 'MAXIMUM' || filterTypeName === 'MINIMUM' || filterTypeName === 'TEXT') {
      return FilterType._7;
    }

    if (filterTypeName === 'MULTI_SELECT' || filterTypeName === 'MULTI_SELECT_NOFILTER') {
      return FilterType._1;
    }

    return fallbackFilterType;
  }

  private cloneFilterModels(filters: PipingSearchFilterModel[]): PipingSearchFilterModel[] {
    return (filters ?? []).map((filter: PipingSearchFilterModel) => {
      const clonedFilter: PipingSearchFilterModel = new PipingSearchFilterModel(filter);
      clonedFilter.options = [...(filter.options ?? [])];
      return clonedFilter;
    });
  }

  private hydrateFiltersFromSearchResponse(): void {
    if (!this.metadataReadySubject.getValue()) {
      return;
    }

    const normalizedSearchFilters: PipingSearchFilterModel[] = this.normalizePdfSearchFilters(this.latestSearchFiltersFromResponse);
    const searchWasUnfiltered: boolean = (this.filterableProperties ?? []).length === 0;
    const hasBaseFilters: boolean = this.filtersForTypeSubject.getValue().length > 0;

    if (searchWasUnfiltered || !hasBaseFilters) {
      this.filtersForTypeSubject.next(this.cloneFilterModels(normalizedSearchFilters));
    }

    this.facetValues.next(this.cloneFilterModels(normalizedSearchFilters));
  }

  //This is the control point for if we want to count filters with empty options
  private setFilterCounts(pipingSearchFilterModels: PipingSearchFilterModel[]) {
    this.pipeFilterCount = pipingSearchFilterModels.filter(
      (filterModel) => filterModel.filterCategory === this._pipeFilter
    ).length;
    this.valveFilterCount = pipingSearchFilterModels.filter(
      (filterModel) => filterModel.filterCategory === this._valveFilter
    ).length;
    this.commonFilterCount = pipingSearchFilterModels.filter(
      (filterModel) => filterModel.filterCategory === this._commonFilter || filterModel.filterCategory === this._defaultFilter
    ).length;
  }

  private getPipingFilters(): Observable<PipingSearchFilterModel[]> {
    return combineLatest([
      this.facetValues$,
      this.filtersForType$,
      this.latestFilterSelection$,
      this.metadataReady$,
    ]).pipe(
      map((values) => {
        const facets = values[0];
        const filters = values[1];
        const latestFilterSelection = values[2];
        const metadataReady = values[3];

        if (!metadataReady) {
          return [];
        }

        filters.forEach((filter) => {
          if (this.isFilterBeingRemoved || filter.filterPropertyName != latestFilterSelection) {
            const matchingFacets = facets.filter(
              (facet) => facet.filterPropertyName === filter.filterPropertyName
            );
            if (matchingFacets[0] != null) {
              filter.options = matchingFacets[0].options;
              filter.minValue = matchingFacets[0].minValue;
              filter.maxValue = matchingFacets[0].maxValue;

              filter.options.sort((a, b) =>
                a.localeCompare(b, undefined, {
                  numeric: true,
                  sensitivity: 'base',
                })
              );
            }
          }
        });

        filters.sort((a, b) => a.displayOrder - b.displayOrder);
        this.setFilterCounts(filters);
        return filters;
      })
    );
  }

  public updateObjectTypeSelection(clickedElementId: string) {
    const valvesChecked: boolean =
      this.valvesRadioButtonInput.nativeElement.checked;
    const pipesChecked: boolean =
      this.pipesRadioButtonInput.nativeElement.checked;

    if (valvesChecked && pipesChecked) {
      this.filterDataService.removeAllFilters();
      this.typeSelectedSubject.next(this._both);
    } else if (valvesChecked && !pipesChecked) {
      this.typeSelectedSubject.next(this._valve);
    } else if (pipesChecked && !valvesChecked) {
      this.typeSelectedSubject.next(this._pipe);
    } else if (!valvesChecked && !pipesChecked) {
      if (clickedElementId == this._pipesCheckBoxId) {
        this.valvesRadioButtonInput.nativeElement.checked = true;
        this.typeSelectedSubject.next(this._valve);
      } else if (clickedElementId == this._valvesCheckBoxId) {
        this.pipesRadioButtonInput.nativeElement.checked = true;
        this.typeSelectedSubject.next(this._pipe);
      }
    }
    this.setObjectTypeFilter();
  }

  private getSelectedOperatorStringFromSymbol(symbol: string): string {
    if (symbol === '≥') {
      return 'ge '
    } else if (symbol === '>') {
      return 'gt ';
    } else if (symbol === '≤') {
      return 'le ';
    } else if (symbol === '<') {
      return 'lt ';
    } else {
      return 'eq ';
    }
  }

  //! this is the control point for the wildcard behavior
  private wrapSearchTextInWildcardsConditionally(searchText: string): string {
    let returnString = '';
    let notArray = searchText.split("+-");
    let andArray = searchText.split("+");

    if (notArray.length > 1) {
      notArray = notArray.map((element) => element.trim());
      for (let i = 0; i < notArray.length; i++) {
        if (i !== notArray.length - 1) {
          returnString = returnString + notArray[i] + "+-";
        } else {
          returnString = returnString + notArray[i];
        }
      }
    }
    else if (andArray.length > 1) {
      andArray = andArray.map((element) => element.trim())
      for (let i = 0; i < andArray.length; i++) {
        if (i !== andArray.length - 1) {
          returnString = returnString + andArray[i] + "* + ";
        } else {
          returnString = returnString + andArray[i];
        }
      }
    }
    else {
      //GETTING RID OF ALL ASTERISK AND ADDING ONE AT THE BOTTOM
      returnString = searchText.split('*').join('') + "*";
    }
    return returnString;
  }

  public updatePage(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
    this.search();
  }

  resetPage() {
    this.pipesRadioButtonInput.nativeElement.checked = true;
    this.valvesRadioButtonInput.nativeElement.checked = true;
    this.typeSelectedSubject.next(this._both);
  }

  resetFilters() {
    this.resetPage();
    this.filterDataService.removeAllFilters();
  }

  exportAllToPdf() {
    alert('export all functionality not yet implemented');
  }

  public hideShowArchivedDraftChip(filterableProperties: FilterableProperty[]) {
    const allButArchivedDraftFilter = filterableProperties.filter(filter => filter.propertyName !== 'IsArchivedOrDraft');
    const archivedDraftFilter = filterableProperties.filter(filter => filter.propertyName === 'IsArchivedOrDraft')[0];

    if (archivedDraftFilter === undefined || archivedDraftFilter.filterOptions[0] === '1') {
      const filterableProperty = FilterablePropertyFactory.createFilterbleProperty(
        FilterType._6,
        'IsArchivedOrDraft',
        'Archived and Drafts'
      );
      // filterableProperty.filterOptions = ['Archived and Drafts']
      allButArchivedDraftFilter.push(filterableProperty);
    }

    return allButArchivedDraftFilter;
  }
}
